import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { computeZoomAtPoint } from "#/features/treeViewer/lib";
import { MAX_ZOOM, MIN_ZOOM, ZOOM_STEP } from "#/features/treeViewer/model/editorConstants";
type TouchListLike = {
  length: number;
  0?: Touch;
  1?: Touch;
};
function getTouchDistance(touches: TouchListLike): number {
  if (touches.length < 2) return 0;
  const t0 = touches[0];
  const t1 = touches[1];
  if (t0 == null || t1 == null) return 0;
  return Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
}
function getTouchCenter(touches: TouchListLike): {
  clientX: number;
  clientY: number;
} {
  const t0 = touches[0];
  if (touches.length < 2) {
    return {
      clientX: t0?.clientX ?? 0,
      clientY: t0?.clientY ?? 0
    };
  }
  const t1 = touches[1];
  if (t0 == null || t1 == null) {
    return {
      clientX: t0?.clientX ?? t1?.clientX ?? 0,
      clientY: t0?.clientY ?? t1?.clientY ?? 0
    };
  }
  return {
    clientX: (t0.clientX + t1.clientX) / 2,
    clientY: (t0.clientY + t1.clientY) / 2
  };
}
const WHEEL_SYNC_DELAY_MS = 80;
const DEFAULT_VIEW = {
  offsetX: 0,
  offsetY: 0,
  scale: 1
};
export type ViewerPanHandle = {
  resetView: () => void;
  resetOffset: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  isViewAtDefault: boolean;
};
export type UseViewerPanOptions = {
  ref?: React.RefObject<ViewerPanHandle | null>;
};
export const useViewerPan = options => {
  const $ = _c(27);
  const handleRef = options?.ref;
  const [isViewAtDefault, setIsViewAtDefault] = useState(true);
  const [panEvent, setPanEvent] = useState(null);
  const prevEventRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...DEFAULT_VIEW
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const viewRef = useRef(t0);
  const transformRef = useRef(null);
  const viewerRef = useRef(null);
  const wheelSyncTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const pinchRef = useRef(null);
  const touchPanRef = useRef(null);
  const mousePanRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (offsetX, offsetY, scale) => {
      viewRef.current = {
        offsetX,
        offsetY,
        scale
      };
      if (transformRef.current) {
        transformRef.current.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const applyTransform = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (offsetX_0, offsetY_0, scale_0) => {
      applyTransform(offsetX_0, offsetY_0, scale_0);
      setIsViewAtDefault(offsetX_0 === 0 && offsetY_0 === 0 && scale_0 === 1);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const setView = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = grabbing => {
      if (viewerRef.current) {
        viewerRef.current.style.cursor = grabbing ? "grabbing" : "grab";
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const setCursor = t3;
  let t4;
  if ($[4] !== panEvent) {
    t4 = ev => {
      if (!panEvent) {
        return;
      }
      const prev = prevEventRef.current;
      const pan = mousePanRef.current;
      if (prev && pan) {
        pan.accumulatedX = pan.accumulatedX + (ev.clientX - prev.clientX);
        pan.accumulatedY = pan.accumulatedY + (ev.clientY - prev.clientY);
        const {
          scale: scale_1
        } = viewRef.current;
        applyTransform(pan.baseOffsetX + pan.accumulatedX, pan.baseOffsetY + pan.accumulatedY, scale_1);
      }
      prevEventRef.current = ev;
    };
    $[4] = panEvent;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleMouseMove = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ev_0 => {
      if (!(ev_0.target instanceof HTMLDivElement) || ev_0.target.classList.contains("os-scrollbar-handle")) {
        return;
      }
      const {
        offsetX: offsetX_1,
        offsetY: offsetY_1
      } = viewRef.current;
      mousePanRef.current = {
        baseOffsetX: offsetX_1,
        baseOffsetY: offsetY_1,
        accumulatedX: 0,
        accumulatedY: 0
      };
      prevEventRef.current = ev_0;
      setPanEvent(ev_0);
      setCursor(true);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handlePanStart = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const pan_0 = mousePanRef.current;
      if (pan_0) {
        setView(pan_0.baseOffsetX + pan_0.accumulatedX, pan_0.baseOffsetY + pan_0.accumulatedY, viewRef.current.scale);
        mousePanRef.current = null;
      }
      setPanEvent(null);
      prevEventRef.current = null;
      setCursor(false);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handlePanEnd = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const {
        scale: scale_2
      } = viewRef.current;
      setView(0, 0, scale_2);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handlePanReset = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setView(0, 0, 1);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handleViewReset = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const {
        offsetX: offsetX_2,
        offsetY: offsetY_2,
        scale: scale_3
      } = viewRef.current;
      const newScale = Math.min(MAX_ZOOM, scale_3 + ZOOM_STEP);
      setView(offsetX_2, offsetY_2, newScale);
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleZoomIn = t9;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const {
        offsetX: offsetX_3,
        offsetY: offsetY_3,
        scale: scale_4
      } = viewRef.current;
      const newScale_0 = Math.max(MIN_ZOOM, scale_4 - ZOOM_STEP);
      setView(offsetX_3, offsetY_3, newScale_0);
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const handleZoomOut = t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = ev_1 => {
      const target = ev_1.currentTarget;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const rect = target.getBoundingClientRect();
      const {
        offsetX: offsetX_4,
        offsetY: offsetY_4,
        scale: scale_5
      } = viewRef.current;
      const delta = -Math.sign(ev_1.deltaY) * 0.15;
      const newScale_1 = scale_5 * (1 + delta);
      ev_1.preventDefault();
      const next = computeZoomAtPoint({
        offsetX: offsetX_4,
        offsetY: offsetY_4,
        scale: scale_5
      }, ev_1.clientX, ev_1.clientY, rect.left, rect.top, newScale_1);
      applyTransform(next.offsetX, next.offsetY, next.scale);
      if (wheelSyncTimeoutRef.current) {
        clearTimeout(wheelSyncTimeoutRef.current);
      }
      wheelSyncTimeoutRef.current = setTimeout(() => {
        wheelSyncTimeoutRef.current = null;
        if (isMountedRef.current) {
          setView(next.offsetX, next.offsetY, next.scale);
        }
      }, WHEEL_SYNC_DELAY_MS);
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const handleWheel = t11;
  let t12;
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      isMountedRef.current = true;
      const el = viewerRef.current;
      if (el) {
        el.addEventListener("wheel", handleWheel, {
          passive: false
        });
      }
      return () => {
        isMountedRef.current = false;
        if (el) {
          el.removeEventListener("wheel", handleWheel);
        }
        if (wheelSyncTimeoutRef.current) {
          clearTimeout(wheelSyncTimeoutRef.current);
          wheelSyncTimeoutRef.current = null;
        }
      };
    };
    t13 = [handleWheel];
    $[13] = t12;
    $[14] = t13;
  } else {
    t12 = $[13];
    t13 = $[14];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      applyTransform(viewRef.current.offsetX, viewRef.current.offsetY, viewRef.current.scale);
    };
    t15 = [applyTransform];
    $[15] = t14;
    $[16] = t15;
  } else {
    t14 = $[15];
    t15 = $[16];
  }
  useEffect(t14, t15);
  let t16;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = ev_2 => {
      const target_0 = ev_2.currentTarget;
      if (!(target_0 instanceof HTMLElement)) {
        return;
      }
      if (ev_2.touches.length === 2) {
        ev_2.preventDefault();
        touchPanRef.current = null;
        const initialDistance = getTouchDistance(ev_2.touches);
        if (initialDistance <= 0) {
          return;
        }
        const base = {
          ...viewRef.current
        };
        pinchRef.current = {
          initialDistance,
          baseTransform: base,
          center: getTouchCenter(ev_2.touches),
          containerRect: target_0.getBoundingClientRect(),
          lastApplied: base
        };
      } else {
        if (ev_2.touches.length === 1 && !pinchRef.current) {
          const t0$0 = ev_2.touches[0];
          if (t0$0) {
            const {
              offsetX: offsetX_5,
              offsetY: offsetY_5
            } = viewRef.current;
            touchPanRef.current = {
              startX: t0$0.clientX,
              startY: t0$0.clientY,
              baseOffsetX: offsetX_5,
              baseOffsetY: offsetY_5,
              accumulatedX: 0,
              accumulatedY: 0
            };
          }
        }
      }
    };
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  const handleTouchStart = t16;
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = ev_3 => {
      if (ev_3.touches.length === 2 && pinchRef.current) {
        ev_3.preventDefault();
        const pinch = pinchRef.current;
        if (pinch.initialDistance <= 0) {
          return;
        }
        const distance = getTouchDistance(ev_3.touches);
        const center = getTouchCenter(ev_3.touches);
        const scaleFactor = distance / pinch.initialDistance;
        const newScale_2 = pinch.baseTransform.scale * scaleFactor;
        const next_0 = computeZoomAtPoint(pinch.baseTransform, center.clientX, center.clientY, pinch.containerRect.left, pinch.containerRect.top, newScale_2);
        pinch.lastApplied = next_0;
        applyTransform(next_0.offsetX, next_0.offsetY, next_0.scale);
      } else {
        if (ev_3.touches.length === 1 && touchPanRef.current && !pinchRef.current) {
          const t0_0 = ev_3.touches[0];
          if (!t0_0) {
            return;
          }
          const pan_1 = touchPanRef.current;
          pan_1.accumulatedX = pan_1.accumulatedX + (t0_0.clientX - pan_1.startX);
          pan_1.accumulatedY = pan_1.accumulatedY + (t0_0.clientY - pan_1.startY);
          pan_1.startX = t0_0.clientX;
          pan_1.startY = t0_0.clientY;
          const {
            scale: scale_6
          } = viewRef.current;
          applyTransform(pan_1.baseOffsetX + pan_1.accumulatedX, pan_1.baseOffsetY + pan_1.accumulatedY, scale_6);
        }
      }
    };
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  const handleTouchMove = t17;
  let t18;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = ev_4 => {
      if (ev_4.touches.length < 2) {
        const pinch_0 = pinchRef.current;
        if (pinch_0) {
          setView(pinch_0.lastApplied.offsetX, pinch_0.lastApplied.offsetY, pinch_0.lastApplied.scale);
          touchPanRef.current = null;
        }
        pinchRef.current = null;
      }
      if (ev_4.touches.length === 0) {
        const pan_2 = touchPanRef.current;
        if (pan_2) {
          setView(pan_2.baseOffsetX + pan_2.accumulatedX, pan_2.baseOffsetY + pan_2.accumulatedY, viewRef.current.scale);
        }
        touchPanRef.current = null;
      }
    };
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  const handleTouchEnd = t18;
  let t19;
  let t20;
  if ($[20] !== isViewAtDefault) {
    t19 = () => ({
      resetView: handleViewReset,
      resetOffset: handlePanReset,
      zoomIn: handleZoomIn,
      zoomOut: handleZoomOut,
      isViewAtDefault
    });
    t20 = [handleViewReset, handlePanReset, handleZoomIn, handleZoomOut, isViewAtDefault];
    $[20] = isViewAtDefault;
    $[21] = t19;
    $[22] = t20;
  } else {
    t19 = $[21];
    t20 = $[22];
  }
  useImperativeHandle(handleRef, t19, t20);
  let t21;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => ({
      ...viewRef.current
    });
    $[23] = t21;
  } else {
    t21 = $[23];
  }
  let t22;
  if ($[24] !== handleMouseMove || $[25] !== isViewAtDefault) {
    t22 = {
      handlePanStart,
      handlePanEnd,
      handleMouseMove,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      viewerRef,
      transformRef,
      isViewAtDefault,
      getViewState: t21
    };
    $[24] = handleMouseMove;
    $[25] = isViewAtDefault;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  return t22;
};